import React, { useState } from "react";
import { useWatchlist, useCreateWatch, useDeleteWatch, useReference } from "@/lib/queries";
import { Plus, Trash2, Bookmark, Bell, BellOff } from "lucide-react";
import { toast } from "sonner";
import { formatPrice, formatRelative, RETAILER_COLORS } from "@/lib/cr";

const SELECT_CLS = "w-full bg-[#050505] border border-[#262626] text-white text-sm px-3 py-2 rounded-sm focus:outline-none focus:border-[#EAB308] focus:ring-1 focus:ring-[#EAB308]";
const INPUT_CLS = "w-full bg-[#050505] border border-[#262626] text-white text-sm px-3 py-2 rounded-sm focus:outline-none focus:border-[#EAB308] focus:ring-1 focus:ring-[#EAB308] placeholder:text-[#525252]";
const LABEL_CLS = "text-[10px] uppercase tracking-[0.2em] font-bold text-[#737373] mb-1.5";

const EMPTY = {
  name: "",
  query: "",
  game: "",
  set_name: "",
  product_type: "",
  retailers: [],
  regions: [],
  max_price: "",
  min_deal_score: 0,
  in_stock_only: true,
  notify: true,
};

export default function WatchlistPage() {
  const [form, setForm] = useState(EMPTY);
  const { data: items = [] } = useWatchlist();
  const { data: ref } = useReference();
  const createWatch = useCreateWatch();
  const deleteWatch = useDeleteWatch();

  const sets = form.game && ref?.sets_by_game?.[form.game]
    ? ref.sets_by_game[form.game]
    : Object.values(ref?.sets_by_game || {}).flat();

  const submit = (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error("Watch name is required");
      return;
    }
    const payload = {
      ...form,
      max_price: form.max_price === "" ? null : Number(form.max_price),
      min_deal_score: Number(form.min_deal_score) || null,
    };
    createWatch.mutate(payload, {
      onSuccess: () => {
        toast.success("Watch created", { description: form.name });
        setForm(EMPTY);
      },
      onError: () => toast.error("Failed to create watch"),
    });
  };

  const toggleArray = (key, value) => {
    const arr = form[key];
    setForm({ ...form, [key]: arr.includes(value) ? arr.filter((v) => v !== value) : [...arr, value] });
  };

  return (
    <div className="p-6 max-w-7xl w-full mx-auto" data-testid="watchlist-page">
      <header className="mb-6">
        <h1 className="font-heading text-4xl tracking-tighter font-black uppercase">
          Watch<span className="text-[#EAB308]">list</span>
        </h1>
        <p className="text-sm text-[#A3A3A3] mt-1">
          Create rules. The scanner fires an alert the second a match appears in the <span className="text-[#EAB308]">Live Alerts</span> column on <a href="/app/radar" className="text-[#EAB308] hover:underline">Radar</a> (and your Discord/Telegram if configured in <a href="/app/settings" className="text-[#EAB308] hover:underline">Settings</a>).
        </p>
      </header>

      {/* First-time guidance — only visible when the user has zero watches */}
      {items.length === 0 && (
        <div className="bg-[#22C55E]/5 border border-[#22C55E]/30 p-4 rounded-sm mb-6 flex gap-3" data-testid="watch-howto">
          <Bell className="w-5 h-5 text-[#22C55E] shrink-0 mt-0.5" />
          <div className="text-[12px] leading-relaxed text-[#D4D4D4]">
            <strong className="text-white">Tip:</strong> Start broad — pick a Game (e.g. Pokemon) and skip the other filters. You&apos;ll see hits within ~15 seconds on the Radar page. Add more filters later if you&apos;re drowning in alerts.
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <form
          onSubmit={submit}
          className="bg-[#0d0d0d] border border-[#262626] p-5 rounded-sm space-y-4"
          data-testid="watch-form"
        >
          <h2 className="font-heading text-lg font-bold uppercase tracking-wider flex items-center gap-2">
            <Plus className="w-4 h-4 text-[#EAB308]" />
            New Watch
          </h2>

          <div>
            <div className={LABEL_CLS}>Watch name *</div>
            <input
              data-testid="watch-name-input"
              className={INPUT_CLS}
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="e.g. Surging Sparks ETB hunt"
            />
          </div>

          <div>
            <div className={LABEL_CLS}>Keyword (optional)</div>
            <input
              data-testid="watch-query-input"
              className={INPUT_CLS}
              value={form.query}
              onChange={(e) => setForm({ ...form, query: e.target.value })}
              placeholder="charizard, terapagos..."
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <div className={LABEL_CLS}>Game</div>
              <select
                data-testid="watch-game-select"
                id="watch-game-select"
                className={SELECT_CLS}
                value={form.game}
                onChange={(e) => setForm({ ...form, game: e.target.value, set_name: "" })}
              >
                <option value="">Any</option>
                {(ref?.games || []).map((g) => <option key={g} value={g}>{g}</option>)}
              </select>
            </div>
            <div>
              <div className={LABEL_CLS}>Set <span className="text-[#737373] font-normal normal-case tracking-normal">(or type any)</span></div>
              <input
                data-testid="watch-set-input"
                list="watch-set-options"
                className={SELECT_CLS}
                value={form.set_name}
                onChange={(e) => setForm({ ...form, set_name: e.target.value })}
                placeholder="e.g. Surging Sparks, or type a custom set"
                autoComplete="off"
              />
              <datalist id="watch-set-options">
                {sets.map((s) => <option key={s} value={s} />)}
              </datalist>
            </div>
          </div>

          <div>
            <div className={LABEL_CLS}>Product type</div>
            <select
              data-testid="watch-product-type-select"
              className={SELECT_CLS}
              value={form.product_type}
              onChange={(e) => setForm({ ...form, product_type: e.target.value })}
            >
              <option value="">Any</option>
              {(ref?.product_types || []).map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>

          <div>
            <div className={LABEL_CLS}>Retailers</div>
            <div className="flex flex-wrap gap-1.5">
              {(ref?.retailers || []).map((r) => {
                const active = form.retailers.includes(r.id);
                const color = RETAILER_COLORS[r.id];
                return (
                  <button
                    type="button"
                    key={r.id}
                    data-testid={`watch-retailer-${r.id}`}
                    onClick={() => toggleArray("retailers", r.id)}
                    className={`text-[11px] font-mono uppercase tracking-wider px-2 py-1 border rounded-sm transition-all ${
                      active
                        ? "bg-[#1A1A1A]"
                        : "border-[#262626] text-[#737373] hover:text-white hover:border-[#A3A3A3]"
                    }`}
                    style={active ? { color, borderColor: `${color}88` } : {}}
                  >
                    {r.name}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <div className={LABEL_CLS}>Regions</div>
            <div className="flex flex-wrap gap-1.5">
              {(ref?.regions || []).map((r) => {
                const active = form.regions.includes(r);
                return (
                  <button
                    type="button"
                    key={r}
                    data-testid={`watch-region-${r}`}
                    onClick={() => toggleArray("regions", r)}
                    className={`text-[11px] font-mono uppercase tracking-wider px-2 py-1 border rounded-sm transition-all ${
                      active
                        ? "border-[#EAB308] text-[#EAB308] bg-[#EAB308]/10"
                        : "border-[#262626] text-[#737373] hover:text-white hover:border-[#A3A3A3]"
                    }`}
                  >
                    {r}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <div className={LABEL_CLS}>Max price ($)</div>
              <input
                data-testid="watch-max-price"
                type="number"
                min="0"
                className={INPUT_CLS}
                value={form.max_price}
                onChange={(e) => setForm({ ...form, max_price: e.target.value })}
                placeholder="50"
              />
            </div>
            <div>
              <div className={LABEL_CLS}>Min deal score: {form.min_deal_score}</div>
              <input
                data-testid="watch-min-deal-score"
                type="range"
                min="0"
                max="100"
                step="5"
                className="w-full accent-[#EAB308] mt-2"
                value={form.min_deal_score}
                onChange={(e) => setForm({ ...form, min_deal_score: e.target.value })}
              />
            </div>
          </div>

          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 text-xs text-[#A3A3A3] cursor-pointer">
              <input
                type="checkbox"
                data-testid="watch-in-stock"
                checked={form.in_stock_only}
                onChange={(e) => setForm({ ...form, in_stock_only: e.target.checked })}
                className="w-3.5 h-3.5 accent-[#EAB308]"
              />
              In stock only
            </label>
            <label className="flex items-center gap-2 text-xs text-[#A3A3A3] cursor-pointer">
              <input
                type="checkbox"
                data-testid="watch-notify"
                checked={form.notify}
                onChange={(e) => setForm({ ...form, notify: e.target.checked })}
                className="w-3.5 h-3.5 accent-[#EAB308]"
              />
              Send notifications
            </label>
          </div>

          <button
            type="submit"
            data-testid="watch-submit-btn"
            disabled={createWatch.isPending}
            className="w-full bg-[#EAB308] text-black font-bold uppercase tracking-wider text-xs py-2.5 rounded-sm hover:bg-[#FDE047] transition-all flex items-center justify-center gap-2 disabled:opacity-60"
          >
            <Plus className="w-4 h-4" />
            Create watch
          </button>
        </form>

        <div className="space-y-3">
          <h2 className="font-heading text-lg font-bold uppercase tracking-wider flex items-center gap-2">
            <Bookmark className="w-4 h-4 text-[#EAB308]" />
            Active watches ({items.length})
          </h2>

          {items.length === 0 && (
            <div className="border border-dashed border-[#262626] bg-[#0a0a0a] p-8 text-center text-[#737373] rounded-sm text-sm">
              No watches yet. Create your first one.
            </div>
          )}

          {items.map((w) => (
            <div
              key={w.id}
              data-testid={`watch-item-${w.id}`}
              className="bg-[#0d0d0d] border border-[#262626] p-4 rounded-sm card-hover"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    {w.notify ? (
                      <Bell className="w-3.5 h-3.5 text-[#22C55E]" />
                    ) : (
                      <BellOff className="w-3.5 h-3.5 text-[#737373]" />
                    )}
                    <div className="text-sm font-semibold text-white truncate">{w.name}</div>
                  </div>
                  <div className="text-[11px] font-mono text-[#737373] flex flex-wrap gap-x-3 gap-y-0.5">
                    {w.game && <span>{w.game}</span>}
                    {w.set_name && <span>{w.set_name}</span>}
                    {w.product_type && <span>{w.product_type}</span>}
                    {w.max_price && <span className="text-[#EAB308]">≤ {formatPrice(w.max_price)}</span>}
                    {w.min_deal_score > 0 && <span>deal ≥ {w.min_deal_score}</span>}
                  </div>
                  <div className="flex flex-wrap gap-1 mt-2">
                    {w.retailers.map((r) => (
                      <span key={r} className="text-[10px] font-mono uppercase px-1.5 py-0.5 border border-[#262626] text-[#A3A3A3] rounded-sm">
                        {r}
                      </span>
                    ))}
                    {w.regions.map((r) => (
                      <span key={r} className="text-[10px] font-mono uppercase px-1.5 py-0.5 border border-[#EAB308]/30 text-[#EAB308] rounded-sm">
                        {r}
                      </span>
                    ))}
                  </div>
                  <div className="text-[10px] text-[#525252] font-mono mt-2">
                    created {formatRelative(w.created_at)}
                  </div>
                </div>
                <button
                  data-testid={`delete-watch-${w.id}`}
                  onClick={() => {
                    deleteWatch.mutate(w.id, {
                      onSuccess: () => toast.success("Watch removed"),
                    });
                  }}
                  className="p-2 text-[#737373] hover:text-[#EF4444] hover:bg-[#1A1A1A] rounded-sm transition-colors shrink-0"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
